import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent,
  CardMedia,
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import WorkIcon from '@mui/icons-material/Work';
import BusinessIcon from '@mui/icons-material/Business';
import SchoolIcon from '@mui/icons-material/School';
import { useAuth } from '../../contexts/AuthContext';
import { useState, useEffect } from 'react';
import { ROLES } from '../../utils/roles';

function Home() {
  const navigate = useNavigate();
  const { currentUser, userRole } = useAuth();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const isStudentPathDisabled = currentUser && userRole !== ROLES.ETUDIANT;

  const features = [
    {
      icon: <WorkIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: "Offres de Stage",
      description: "Découvrez des opportunités de stage passionnantes auprès d'entreprises innovantes.",
      path: '/offres'
    },
    {
      icon: <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: "Entreprises Partenaires",
      description: "Explorez notre réseau d'entreprises partenaires et trouvez celle qui vous correspond.",
      path: '/entreprises'
    },
    {
      icon: <SchoolIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: "Parcours Étudiant",
      description: "Gérez vos candidatures et votre liste de souhaits en quelques clics.",
      onClick: () => {
        if (!currentUser) {
          navigate('/login');
        } else if (userRole === ROLES.ETUDIANT) {
          navigate('/profil');
        } else {
          setOpenDialog(true);
        }
      },
      isStudentPath: true
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box 
        sx={{ 
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          pt: { xs: 8, md: 12 },
          pb: { xs: 4, md: 6 },
          mb: 4,
          minHeight: { xs: '60vh', md: '80vh' },
          position: 'relative',
          transform: `translateY(${-Math.min(scrollY * 0.3, 200)}px)`,
          transition: 'transform 0.3s ease-out',
        }}
      >
        <Container maxWidth="lg">
          <Grid 
            container 
            spacing={6}
            alignItems="center"
            sx={{ height: '100%' }}
          >
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h2" 
                component="h1" 
                sx={{ 
                  fontWeight: 'bold',
                  mb: 3,
                  fontSize: { xs: '2.5rem', md: '3.75rem' }
                }}
              >
                Trouvez le Stage 
                <br />
                de vos Rêves
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 4,
                  opacity: 0.9,
                  fontSize: { xs: '1.2rem', md: '1.5rem' }
                }}
              >
                La plateforme qui connecte les étudiants aux meilleures opportunités de stage.
              </Typography>
              <Button 
                variant="contained"
                size="large"
                onClick={() => navigate('/offres')}
                sx={{
                  bgcolor: 'primary.contrastText',
                  color: 'primary.main',
                  py: 2,
                  px: 4,
                  fontSize: '1.1rem',
                  '&:hover': {
                    bgcolor: 'primary.dark'
                  }
                }}
              >
                Voir les offres
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box 
                component="img"
                src="/images/hero-image.png"
                alt="Étudiants en stage"
                sx={{
                  width: '100%',
                  maxWidth: 600,
                  height: 'auto',
                  display: 'block',
                  margin: 'auto'
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ mb: 8, mt: -14 }}>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid 
              item 
              xs={12} 
              md={4} 
              key={index}
              sx={{
                opacity: scrollY > 100 ? 1 : 0,
                transform: `translateY(${scrollY > 100 ? 0 : 100}px)`,
                transition: `all 0.8s ease-out ${index * 0.2}s`,
              }}
            >
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: (!feature.isStudentPath || !isStudentPathDisabled) ? 'translateY(-5px)' : 'none',
                    boxShadow: (!feature.isStudentPath || !isStudentPathDisabled) ? 3 : 1
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                  <Box sx={{ mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h5" component="h2" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    {feature.description}
                  </Typography>
                  <Button 
                    variant="outlined"
                    onClick={feature.onClick || (() => navigate(feature.path))}
                    sx={{ 
                      mt: 2,
                      color: (feature.isStudentPath && isStudentPathDisabled) ? 'grey.500' : 'primary.main',
                      borderColor: (feature.isStudentPath && isStudentPathDisabled) ? 'grey.300' : 'primary.main',
                      cursor: (feature.isStudentPath && isStudentPathDisabled) ? 'not-allowed' : 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: (feature.isStudentPath && isStudentPathDisabled) ? 'grey.300' : 'primary.main',
                        bgcolor: (feature.isStudentPath && isStudentPathDisabled) ? 'transparent' : 'primary.main',
                        color: (feature.isStudentPath && isStudentPathDisabled) ? 'grey.500' : 'white',
                        transform: (feature.isStudentPath && isStudentPathDisabled) ? 'none' : 'translateY(-2px)',
                        boxShadow: (feature.isStudentPath && isStudentPathDisabled) ? 'none' : '0 5px 15px rgba(0,0,0,0.1)',
                      }
                    }}
                  >
                    En savoir plus
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Statistics Section */}
      <Box 
        sx={{ 
          bgcolor: 'background.default', 
          py: 8,
          opacity: scrollY > 600 ? 1 : 0,
          transform: `translateY(${scrollY > 600 ? 0 : 50}px)`,
          transition: 'all 0.6s ease-out',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="center">
            {[
              { number: "500+", label: "Offres de stage" },
              { number: "200+", label: "Entreprises partenaires" },
              { number: "1000+", label: "Étudiants placés" }
            ].map((stat, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      fontWeight: 'bold',
                      color: 'primary.main',
                      mb: 1
                    }}
                  >
                    {stat.number}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Accès non autorisé
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Cette section est réservée aux étudiants.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary" autoFocus>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Home;